import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { CircleNotch, CheckCircle, Clock, XCircle } from "@phosphor-icons/react";

export default function StoreSuccess() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [order, setOrder] = useState(null);
  const [tries, setTries] = useState(0);

  useEffect(() => {
    // Clear the local cart on arrival (payment either completed or is processing).
    try { localStorage.removeItem(`hephy_cart_${slug}`); } catch { /* noop */ }
  }, [slug]);

  useEffect(() => {
    if (!sessionId) return;
    let stop = false;
    const poll = () => {
      api.get(`/public/order/${sessionId}`, { withCredentials: false })
        .then((r) => { if (!stop) { setOrder(r.data); if (r.data.status === "pending" && tries < 8) setTimeout(() => setTries((t) => t + 1), 2000); } })
        .catch(() => { if (!stop && tries < 8) setTimeout(() => setTries((t) => t + 1), 2000); });
    };
    poll();
    return () => { stop = true; };
  }, [sessionId, tries]);

  const status = order?.status;
  const paid = status === "paid";
  const failed = status === "failed" || status === "error";

  return (
    <div className="min-h-screen bg-[#0B0B0F] text-neutral-100 flex items-center justify-center px-6" data-testid="store-success-page">
      <div className="max-w-md w-full text-center">
        {!order ? (
          <><CircleNotch size={40} className="animate-spin text-gold mx-auto" /><p className="text-neutral-400 mt-4">{t("success.confirming")}</p></>
        ) : paid ? (
          <div data-testid="order-paid">
            <CheckCircle size={56} weight="fill" className="text-emerald-400 mx-auto" />
            <h1 className="text-2xl font-bold mt-4">{t("success.paidTitle")}</h1>
            <p className="text-neutral-400 mt-2">{t("success.paidBody")} <b className="text-neutral-200">{order.buyer_email}</b>.</p>
            <div className="border border-white/10 rounded-xl p-4 mt-6 text-left bg-white/[0.03]">
              {(order.items || []).map((it, i) => (
                <div key={i} className="flex justify-between text-sm py-1"><span className="text-neutral-300">{it.name} ×{it.quantity}</span><span>${(it.unit_amount * it.quantity).toLocaleString()}</span></div>
              ))}
              <div className="flex justify-between border-t border-white/10 mt-2 pt-2 font-semibold"><span>{t("common.total")}</span><span>${Number(order.amount).toLocaleString()}</span></div>
            </div>
          </div>
        ) : failed ? (
          <div data-testid="order-failed">
            <XCircle size={56} weight="fill" className="text-red-400 mx-auto" />
            <h1 className="text-2xl font-bold mt-4">{t("success.failedTitle")}</h1>
            <p className="text-neutral-400 mt-2">{t("success.failedBody")}</p>
          </div>
        ) : (
          <div data-testid="order-pending">
            <Clock size={56} weight="fill" className="text-gold mx-auto" />
            <h1 className="text-2xl font-bold mt-4">{t("success.pendingTitle")}</h1>
            <p className="text-neutral-400 mt-2">{t("success.pendingBody")} <b className="text-neutral-200">{order.buyer_email}</b>.</p>
          </div>
        )}
        <Link to={`/tienda/${slug}`} className="inline-block mt-8 text-gold hover:underline text-sm">← {t("common.backToStore")}</Link>
      </div>
    </div>
  );
}
